/**
 * A door adapter's write gate, and the only place in any console where
 * it can be armed.
 *
 * Until this existed the ONLY way to arm the MEGAZO adapter was a curl
 * against the pod's own control port, reached by a `kubectl
 * port-forward` from the production node — the same situation
 * `LiftWriteGatePanel` was written to end, one subsystem over, and
 * with less excuse: the door adapter's arm state is never persisted
 * either (invariant I1), so every restart of that pod silently takes
 * every door at the site out of RMF's reach until somebody with
 * cluster access notices and curls it back.
 *
 * PER ADAPTER, NOT PER DOOR (decision D1), and the copy says so
 * wherever it appears. One arm covers every door in the gate's `doors`
 * array — at sg-nomad-01 that is `L4_SLL_Door` AND the sandbox row
 * from a single press — so the confirm dialog reads those names back
 * before the press rather than leaving an operator to discover the
 * scope of their own arm afterwards. The panel enumerates the adapters
 * it is given and never assumes there is one, because the day a site
 * grows a second door adapter is the day a hardcoded first entry starts
 * arming the wrong building's doors.
 *
 * SHADOW MODE IS THIS PANEL'S OWN TRAP, and the lift has no equivalent.
 * `live` is deploy-time and `armed` is runtime, and an adapter that is
 * armed but not live still sends nothing: `translate.door_mode` returns
 * MODE_OFFLINE while `shadow` holds no matter what `armed` says, and
 * `WriteGate.check` refuses every opening action with a SHADOW MODE
 * error that is written to the adapter's log and reaches no browser
 * anywhere. So an operator would arm, see "armed", press Open, and
 * watch nothing happen with no explanation in any console. Arming is
 * disabled here while `shadow` holds, and the panel says why: the fix
 * is a deploy, not a button (decision D2 — `live` is never a toggle).
 */
import { useState } from 'react';
import { StatusChip } from '../../design-system/components/StatusChip';
import { ConfirmDialog } from '../../design-system/components/ConfirmDialog';
import { RoleGuard } from '../../auth/RoleGuard';
import { DOOR_ARM_WRITES_ROLES } from '../../auth/types';
import { useActiveSite } from '../../state/useActiveSite';
import { useArmDoorWrites, useDoorWriteGates } from '../../api/hooks/useDoors';
import type { DoorWriteGate } from '../../api/v1-client';
import {
  armBlockedReason,
  doorGateStatus,
  gateIsReadable,
  NO_ADAPTER,
  UNKNOWN,
  UNSUPPORTED,
  type DoorGateStatus,
} from './door-gate-status';
import './DoorWriteGatePanel.css';

/** Defensive: a gate document that reached here without a `doors`
 *  array is one the confirm dialog cannot name the scope from, and
 *  naming no doors is far better than naming `undefined` ones. */
function doorsOf(gate: DoorWriteGate): readonly string[] {
  return Array.isArray(gate.doors) ? gate.doors : [];
}

/**
 * The shortest phrase that tells one adapter's panel apart from
 * another's — two door names, then a count.
 *
 * Taking the adapter id out of the operator-visible copy was right (it
 * was the vendor's process name), but it left every gate panel with an
 * identical heading, an identical `aria-label` and a confirmation
 * about "this site's door adapter". With one adapter that is latent;
 * with two, a screen-reader user meets two regions called "Door
 * writes" and an arm dialog that does not say which one it arms. The
 * discriminator therefore comes from the DOORS — the only vocabulary
 * an operator can act on, and the same thing the scope line and the
 * dialog already name.
 *
 * Two, not all of them: an accessible name is read aloud in full every
 * time the region is entered, and an adapter owning a whole floor
 * would make that unusable. The full list is in the scope line and in
 * the confirm dialog, both of which are read once and deliberately.
 */
function doorSummary(doors: readonly string[]): string {
  if (doors.length === 0) return '';
  const shown = doors.slice(0, 2).join(', ');
  return doors.length > 2 ? `${shown} and ${doors.length - 2} more` : shown;
}

/** A verdict with no adapter behind it: nothing to arm, and nothing
 *  that may be mistaken for an arm state. */
function GateNotice({ status }: { status: DoorGateStatus }) {
  return (
    <section className="nx-door-gate" aria-label="Door adapter writes">
      <div className="nx-door-gate__head">
        <div>
          <h3 className="nx-door-gate__title">Door adapter writes</h3>
        </div>
        <span className="nx-door-gate__head-end">
          <span data-testid="door-gate-state">
            <StatusChip status={status.chip} label={status.label} />
          </span>
        </span>
      </div>
      <p className="nx-door-gate__meaning" data-testid="door-gate-meaning">
        {status.meaning}
      </p>
      <p className="nx-door-gate__action">{status.action}</p>
    </section>
  );
}

function AdapterGate({
  gate,
  /** More than one gate is on screen, so "this site's door adapter" is
   *  no longer a description of one thing. Only the arm confirmation
   *  reads this: the `aria-label` names its doors either way, because
   *  a region's accessible name is also how it is told apart from the
   *  lift's panel, from a drawer's copy of itself, and from whatever
   *  the page grows next. */
  discriminate = false,
}: {
  gate: DoorWriteGate;
  discriminate?: boolean;
}) {
  const { site } = useActiveSite();
  const arm = useArmDoorWrites();
  const [reason, setReason] = useState('');
  const [confirmingArm, setConfirmingArm] = useState(false);

  const status = doorGateStatus(gate);
  const doors = doorsOf(gate);
  const summary = doorSummary(doors);
  const siteLabel = site?.label ?? 'this site';
  // The subject of the arm sentence. An adapter that has named no
  // doors cannot be told apart by them, and inventing a subject there
  // would be worse than the ambiguity: that branch keeps the original
  // wording and says outright that the console cannot list the scope.
  const adapterPhrase =
    discriminate && summary ? `the door adapter that owns ${summary}` : 'this site’s door adapter';

  // Which control to draw follows the RAW flag, not the verdict, and
  // the asymmetry is deliberate (invariant I2). An armed adapter whose
  // inventory later failed reads as 'unvalidated', and an armed
  // adapter in shadow reads as 'shadow' — but both are still armed,
  // and a supervisor must always be able to stand one down. Making it
  // harder to stop than to start would be backwards, and that includes
  // hiding or disabling the button behind a tier-1 verdict.
  const isArmed = gate.armed === true;
  // Derived from the SAME verdict the chip renders, via the same call
  // that produces the tooltip, so "why is this disabled" and "what
  // does the chip say" cannot answer differently.
  const blockedReason = armBlockedReason(status);
  const canArm = blockedReason === undefined;

  function send(armed: boolean) {
    const trimmed = reason.trim();
    arm.mutate({
      adapterId: gate.adapter_id,
      body: {
        armed,
        // `confirmed` is attached ONLY when arming. Sending
        // `confirmed: false` on a disarm would be harmless today, but
        // it would put a flag on the wire that a future reader could
        // start checking, and a disarm that can be refused for a
        // missing confirmation is invariant I2 broken.
        ...(armed ? { confirmed: true } : {}),
        ...(trimmed ? { reason: trimmed } : {}),
        // `request_id`, `actor`, `ts` and `schema_version` are NOT
        // sent: the api-server mints all four, and its request model
        // forbids extras, so sending one is a 422 rather than a
        // silently ignored field.
        idempotency_key: crypto.randomUUID(),
      },
    });
    setReason('');
  }

  const submitted = arm.data;
  const scopeSentence =
    doors.length > 0
      ? `One arm covers every door this adapter owns — ${doors.join(', ')} — not one door.`
      : 'This adapter has not named the doors it owns, so the console cannot say what an arm would cover.';

  return (
    <section
      className="nx-door-gate"
      // The adapter's raw id is deliberately absent from every string
      // an operator reads: it is an internal identifier, and the scope
      // line below says what this panel governs in the only vocabulary
      // an operator has — the doors themselves. Still emitted as a
      // non-visible attribute for debugging and for anything that
      // needs to tell two panels apart mechanically.
      //
      // The accessible name carries the doors for the same reason the
      // scope line does: with the id gone, "Door writes" alone named
      // every gate panel identically, and a screen-reader user meeting
      // two of them had nothing to choose by. The heading stays short
      // — a sighted operator reads the scope line an inch below it —
      // and this is the half that has to stand on its own.
      aria-label={summary ? `Door writes — ${summary}` : 'Door writes'}
      data-adapter-id={gate.adapter_id}
    >
      <div className="nx-door-gate__head">
        <div>
          <h3 className="nx-door-gate__title">Door writes</h3>
          <p className="nx-door-gate__scope" data-testid="door-gate-scope">
            {scopeSentence}
          </p>
        </div>
        <span className="nx-door-gate__head-end">
          <span data-testid="door-gate-state">
            <StatusChip status={status.chip} label={status.label} />
          </span>
        </span>
      </div>

      <p className="nx-door-gate__meaning" data-testid="door-gate-meaning">
        {status.meaning}
      </p>
      <p className="nx-door-gate__action">{status.action}</p>
      {status.detail && (
        <p className="nx-door-gate__detail" data-testid="door-gate-detail">
          Adapter reports: {status.detail}
        </p>
      )}
      {!gate.link_ok && (
        // Not a gate tier and not a reason to refuse an arm: the link
        // can come back without anyone touching arm state. But an
        // armed adapter with a dead link opens nothing either, and an
        // operator staring at an "Armed" chip while Open does nothing
        // deserves to see the other half of the answer.
        <p className="nx-door-gate__detail" data-testid="door-gate-link">
          The adapter reports its {gate.transport || 'vendor'} link is down. Arm state is
          unaffected, but nothing reaches a door while it stays down.
        </p>
      )}

      <RoleGuard
        allowedRoles={[...DOOR_ARM_WRITES_ROLES]}
        fallback={
          <p className="nx-door-gate__scope">
            Arming takes a site supervisor or a platform administrator.
          </p>
        }
      >
        <div className="nx-door-gate__controls">
          <label className="nx-door-gate__reason">
            Reason (optional, kept in the audit trail)
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isArmed ? 'e.g. shift over, standing the doors down' : 'e.g. commissioning run, level 4'}
              data-testid="door-arm-reason"
            />
          </label>

          {/* Both controls, not one or the other, and the disarm is
              drawn on the raw flag alone. An adapter that is armed AND
              blocked (bad inventory, or shadow) still needs standing
              down, and that press must never be behind a verdict.  */}
          {isArmed ? (
            <button
              type="button"
              className="nx-door-gate__btn nx-door-gate__btn--disarm"
              data-testid="door-disarm-btn"
              disabled={arm.isPending}
              // No confirmation step, deliberately (invariant I2). The
              // document a disarm builds carries no `confirmed` key at
              // all, for the same reason: a disarm during an incident
              // must never be refusable on a missing flag.
              onClick={() => send(false)}
            >
              {arm.isPending ? 'Disarming…' : 'Disarm door writes'}
            </button>
          ) : (
            <button
              type="button"
              className="nx-door-gate__btn nx-door-gate__btn--arm"
              data-testid="door-arm-btn"
              disabled={!canArm || arm.isPending}
              title={blockedReason}
              onClick={() => setConfirmingArm(true)}
            >
              {arm.isPending ? 'Arming…' : 'Arm door writes'}
            </button>
          )}

          {arm.isError && (
            <p className="nx-door-gate__error" role="alert">
              {arm.error instanceof Error ? arm.error.message : 'The request failed.'}
            </p>
          )}
          {submitted && (
            <p className="nx-door-gate__scope" data-testid="door-arm-submitted" role="status">
              {/* ACKNOWLEDGED, never "Armed". All the api-server can
                  say is that the request was authorised and handed to
                  the worker; the adapter accepts or refuses on its own
                  log and republishes its gate separately. Reporting a
                  request as though it were the live flag is the same
                  mistake that left six lifts reading MODE_OFFLINE. */}
              {submitted.requested_armed ? 'Arm' : 'Disarm'} request {submitted.request_id}{' '}
              acknowledged — not confirmed.{' '}
              {gateIsReadable(status)
                ? 'The adapter may still refuse it, and it refuses to its own log, never to this console. The state above is the only confirmation: if it does not change, treat the request as refused.'
                : 'Nothing here can tell you whether the adapter accepted it, because its gate cannot be read from this console. Confirm at the adapter before acting as though it took.'}
            </p>
          )}
        </div>
      </RoleGuard>

      <ConfirmDialog
        open={confirmingArm}
        title="Arm door writes?"
        // NAMES THE DOORS, every time (decision D1). The scope of an
        // arm is the adapter's whole `doors` list and there is no
        // per-door arming, so an operator who was not shown the names
        // here would only discover what they armed by watching a door
        // they did not expect open.
        //
        // It no longer names the ADAPTER (2026-09-03): the internal id
        // -- `megazo-icadm-door-adapter` -- was the vendor's process
        // name, which told an operator nothing they could act on while
        // leaking an implementation detail across the whole panel. The
        // door names ARE the safety content of this sentence and stay
        // exactly as they were; what got armed is still unambiguous.
        //
        // With a SECOND gate on screen, though, "this site's door
        // adapter" stopped describing one thing, and this dialog is
        // the last moment before a real door can be commanded. So the
        // subject becomes the doors as well (`adapterPhrase`) —
        // identifying the adapter by what it opens rather than by a
        // process name, which is the same trade the aria-label makes.
        message={
          doors.length > 0
            ? `Arming door writes allows ${adapterPhrase} to command: ${doors.join(', ')}. That is every door it owns at ${siteLabel} — one arm covers all of them, and there is no per-door arming. It holds until someone disarms it or the adapter restarts. Disarming afterwards takes one click and no confirmation.`
            : `Arming door writes allows ${adapterPhrase} to command every door it owns at ${siteLabel}, and it has not named them, so this console cannot tell you which doors that is. Check the adapter’s inventory before arming.`
        }
        confirmLabel="Arm"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          setConfirmingArm(false);
          send(true);
        }}
        onCancel={() => setConfirmingArm(false)}
      />
    </section>
  );
}

/**
 * Every door adapter's gate at this site, or — with `door` — only the
 * adapters that own that one door.
 *
 * The `door` filter is what lets the drawer render THE SAME COMPONENT
 * over THE SAME QUERY as the panel above the table, rather than a
 * second, smaller reading of arm state. Two readings of one flag in
 * one screen is the bug this panel exists to prevent, and it is
 * exactly why the lift does it this way (`DevicesScreen.tsx`).
 */
export function DoorWriteGatePanel({ door }: { door?: string }) {
  const gatesQuery = useDoorWriteGates();
  const gates = gatesQuery.data;

  // `null`, `undefined` and `[]` are three different answers and must
  // stay that way. `null` is `fetchDoorWriteGates` reporting a 404 —
  // there are no door gates on this api-server; `undefined` is the
  // query not having an answer yet, whether in flight or failed; `[]`
  // is a successful read of a site with no door adapter. None of the
  // three is a gate, and none may be rendered as one.
  if (gates === null) return <GateNotice status={UNSUPPORTED} />;
  if (gates === undefined) return <GateNotice status={UNKNOWN} />;

  const scoped = door === undefined ? gates : gates.filter((g) => doorsOf(g).includes(door));

  if (scoped.length === 0) {
    // With a door in hand this is not a fault and must not read as
    // one: a door driven by something other than a gated adapter — the
    // door mux, an edge-side integration — belongs to no adapter's
    // `doors` list and never will.
    if (door !== undefined) {
      return (
        <p className="nx-door-gate__orphan" data-testid="door-gate-no-adapter">
          No door adapter at this site lists {door} in its write gate, so adapter arming says
          nothing about this door. Whatever drives it is not a gated adapter this console can see.
        </p>
      );
    }
    return <GateNotice status={NO_ADAPTER} />;
  }

  return (
    <>
      {scoped.map((gate) => (
        <AdapterGate
          key={gate.adapter_id}
          gate={gate}
          discriminate={scoped.length > 1}
        />
      ))}
    </>
  );
}
